import StandardLayout from '@/common/layouts/StandardLayout';
import { Row, Col, Button, Form, FormGroup, Label, Input, Card, CardBody, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  bulkAdminTransfer,
  removeUser,
  updateUser,
  selectTotalBulkTransferAmount,
  selectBulkTransferUsers,
  selectUserTransfer,
  addUserBulkTransfer,
  selectBulkTransferState,
  setTransactionType,
  selectBulkTransferTransactionType,
} from '@/redux/slices/bulkTransferSlice';
import { ChangeEvent, ReactNode, useMemo } from 'react';
import { FaExternalLinkAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { AppState } from '@/redux/root';
import { config, AdminBulkTransferUser, TransactionType } from '@repo/common-types';
import { Link } from 'react-router-dom';

const BulkTransformUserForm = ({ index }: { index: number }) => {
  const userTransfer: AdminBulkTransferUser = useSelector((state: AppState) => selectUserTransfer(state, index));
  const dispatch = useDispatch();
  const bulkTransferState = useSelector(selectBulkTransferState);

  const handleChange = (value: string | number, field: 'alias' | 'amount') => {
    dispatch(updateUser({ index, user: { ...userTransfer, [field]: value } }));
  };

  return (
    <Card key={index} className="bg-dark-subtle mb-3 border-0 shadow-sm">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            {userTransfer.error && (
              <div className="alert alert-danger mb-2 py-2" role="alert">
                {userTransfer.error}
              </div>
            )}
            {userTransfer.transactionId && userTransfer.transactionId !== null && (
              <div className="alert alert-success d-flex align-items-center mb-2 gap-2 py-2">
                <Link
                  target="_blank"
                  rel="noopener"
                  className="text-truncate text-white"
                  to={`${config.deposits.polygon.blockExplorerUrl}/tx/${userTransfer.transactionId}`}
                >
                  {userTransfer.transactionId}
                </Link>
                <FaExternalLinkAlt size={12} />
              </div>
            )}
          </div>
          <Button
            color="link"
            className="text-muted ms-3 p-0"
            onClick={() => dispatch(removeUser(index))}
            disabled={!!userTransfer.transactionId || bulkTransferState.loading}
            aria-label="Remove recipient"
          >
            <FaTrash size={16} />
          </Button>
        </div>
        <Row className="g-3">
          <Col md={6}>
            <FormGroup>
              <Label for={`alias-${userTransfer.userId}`}>Alias</Label>
              <Input
                id={`alias-${userTransfer.userId}`}
                type="text"
                value={userTransfer.alias}
                onChange={(e) => handleChange(e.target.value, 'alias')}
                placeholder="Enter alias"
                disabled={!!userTransfer.transactionId || bulkTransferState.loading}
                invalid={!!userTransfer.error}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for={`amount-${userTransfer.userId}`}>Amount</Label>
              <div className="d-flex">
                <span className="input-group-text border-end-0 text-muted bg-transparent">USDT</span>
                <Input
                  id={`amount-${userTransfer.userId}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={userTransfer.amount || ''}
                  onChange={(e) => handleChange(e.target.value === '' ? 0 : parseFloat(e.target.value), 'amount')}
                  placeholder="0.00"
                  disabled={!!userTransfer.transactionId || bulkTransferState.loading}
                  invalid={!!userTransfer.error}
                  className="border-start-0"
                />
              </div>
            </FormGroup>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const SummaryMetric = ({
  label,
  value,
  highlightClass,
}: {
  label: string;
  value: ReactNode;
  highlightClass?: string;
}) => (
  <div className="d-flex flex-column">
    <span className="text-uppercase text-muted small fw-semibold">{label}</span>
    <span className={`fs-4 fw-semibold ${highlightClass ?? ''}`}>{value}</span>
  </div>
);

const TRANSACTION_TYPE_OPTIONS: { value: TransactionType; label: string }[] = [
  { value: TransactionType.USDT_TRANSFER, label: 'Admin Transfer' },
];

const BulkTransfersPage = () => {
  const dispatch = useDispatch();

  const users: AdminBulkTransferUser[] = useSelector(selectBulkTransferUsers);
  const totalAmount: number = useSelector(selectTotalBulkTransferAmount);
  const bulkTransferState = useSelector(selectBulkTransferState);
  const transactionType = useSelector(selectBulkTransferTransactionType);

  const handleTransactionTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setTransactionType(event.target.value as TransactionType));
  };

  const handleSave = () => {
    dispatch(bulkAdminTransfer({ users, transactionType }));
  };

  const bulkTransferValid = useMemo(() => {
    if (users.some((u) => !u.alias || !u.amount || u.amount <= 0)) {
      return false;
    }
    if (totalAmount <= 0) {
      return false;
    }
    return true;
  }, [users, totalAmount]);

  return (
    <StandardLayout pageTitle="Bulk Admin Transfer">
      <Row className="p-4">
        <Col>
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end mb-4 gap-3">
            <div>
              <h5 className="mb-0">Bulk Admin Transfer</h5>
              <small className="text-muted">Send USDT to multiple recipients with the selected transaction type.</small>
            </div>
            <FormGroup className="w-100 w-lg-50 mb-0">
              <Label for="bulk-transaction-type" className="text-uppercase text-muted small mb-1">
                Transaction Type
              </Label>
              <Input
                id="bulk-transaction-type"
                type="select"
                value={transactionType}
                onChange={handleTransactionTypeChange}
                disabled={bulkTransferState.loading}
              >
                {TRANSACTION_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>

          <Form>
            {users.map((user, index) => (
              <BulkTransformUserForm key={index} index={index} />
            ))}

            <div className="d-flex justify-content-start">
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => dispatch(addUserBulkTransfer())}
                className="d-flex align-items-center mt-2 gap-2"
                disabled={bulkTransferState.loading}
              >
                <FaPlus /> Add recipient
              </Button>
            </div>

            <Card className="bg-dark-subtle mt-4 border-0 shadow-sm">
              <CardBody className="px-4 py-3">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-4">
                  <div className="d-flex flex-wrap gap-4">
                    <SummaryMetric label="Players" value={users.filter((u) => u.alias && u.amount).length} />
                    <SummaryMetric
                      label="Total"
                      value={`${totalAmount.toFixed(2)} USDT`}
                      highlightClass={totalAmount > 1000 ? 'text-danger' : 'text-white'}
                    />
                  </div>
                  <Button
                    color="primary"
                    className="d-flex align-items-center ms-md-auto gap-2"
                    onClick={handleSave}
                    disabled={!bulkTransferValid || bulkTransferState.loading || bulkTransferState.error !== null}
                  >
                    Submit Transfer
                    {bulkTransferState.loading && <Spinner size="sm" />}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Form>
        </Col>
      </Row>
    </StandardLayout>
  );
};

export default BulkTransfersPage;
